// ══════════════════════════════════════════════════════════════
//  BEYBLADE AUDIO ENGINE — Layered, realistic metal-on-metal sounds
// ══════════════════════════════════════════════════════════════
const AudioCtx = window.AudioContext || window.webkitAudioContext;
let ctx = null;
let unlocked = false;
let masterGain = null;

export function unlock() {
  if (!ctx) _initCtx();
  if (ctx.state === 'suspended') ctx.resume();
  unlocked = true;
}

function _initCtx() {
  ctx = new AudioCtx();
  masterGain = ctx.createGain();
  masterGain.gain.value = 0.7;
  masterGain.connect(ctx.destination);
}

function ensureCtx() {
  if (!ctx) _initCtx();
  return ctx;
}

// ── Helper: create noise buffer ──
function createNoiseBuffer(duration, decay = 5) {
  const c = ensureCtx();
  const len = c.sampleRate * duration;
  const buf = c.createBuffer(1, len, c.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < len; i++) {
    data[i] = (Math.random() * 2 - 1) * Math.exp(-i / (c.sampleRate / decay));
  }
  return buf;
}

// ══════════════════════════════════════════════════════════════
//  COLLISION — metallic clang with resonance
//  Multiple detuned oscillators + filtered noise + bandpass ring
// ══════════════════════════════════════════════════════════════
export function playCollision(intensity = 1) {
  const c = ensureCtx();
  const now = c.currentTime;
  const dur = 0.2 + intensity * 0.15;

  // Layer 1: metallic ring — two detuned square waves
  const osc1 = c.createOscillator();
  const osc2 = c.createOscillator();
  osc1.type = 'square';
  osc2.type = 'square';
  const baseFreq = 800 + intensity * 600;
  osc1.frequency.setValueAtTime(baseFreq, now);
  osc1.frequency.exponentialRampToValueAtTime(200, now + dur);
  osc2.frequency.setValueAtTime(baseFreq * 1.02, now); // slight detune
  osc2.frequency.exponentialRampToValueAtTime(205, now + dur);

  const ringGain = c.createGain();
  ringGain.gain.setValueAtTime(0.06 * intensity, now);
  ringGain.gain.exponentialRampToValueAtTime(0.001, now + dur);

  // Bandpass filter for metallic resonance
  const bandpass = c.createBiquadFilter();
  bandpass.type = 'bandpass';
  bandpass.frequency.setValueAtTime(2200 + intensity * 800, now);
  bandpass.Q.setValueAtTime(8, now);

  osc1.connect(bandpass);
  osc2.connect(bandpass);
  bandpass.connect(ringGain).connect(masterGain);
  osc1.start(now);
  osc2.start(now);
  osc1.stop(now + dur);
  osc2.stop(now + dur);

  // Layer 2: impact thud — low frequency punch
  const thud = c.createOscillator();
  thud.type = 'sine';
  thud.frequency.setValueAtTime(120 + intensity * 80, now);
  thud.frequency.exponentialRampToValueAtTime(40, now + 0.08);
  const thudGain = c.createGain();
  thudGain.gain.setValueAtTime(0.15 * intensity, now);
  thudGain.gain.exponentialRampToValueAtTime(0.001, now + 0.1);
  thud.connect(thudGain).connect(masterGain);
  thud.start(now);
  thud.stop(now + 0.1);

  // Layer 3: short noise burst — the "clash" texture
  const noiseBuf = createNoiseBuffer(0.06, 15);
  const noise = c.createBufferSource();
  noise.buffer = noiseBuf;
  const noiseGain = c.createGain();
  noiseGain.gain.setValueAtTime(0.12 * intensity, now);
  noiseGain.gain.exponentialRampToValueAtTime(0.001, now + 0.06);
  const hipass = c.createBiquadFilter();
  hipass.type = 'highpass';
  hipass.frequency.setValueAtTime(3000, now);
  noise.connect(hipass).connect(noiseGain).connect(masterGain);
  noise.start(now);
}

// ══════════════════════════════════════════════════════════════
//  WALL HIT — resonant stadium impact
// ══════════════════════════════════════════════════════════════
export function playWallHit() {
  const c = ensureCtx();
  const now = c.currentTime;

  // Deep thump
  const osc = c.createOscillator();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(250, now);
  osc.frequency.exponentialRampToValueAtTime(60, now + 0.12);
  const gain = c.createGain();
  gain.gain.setValueAtTime(0.12, now);
  gain.gain.exponentialRampToValueAtTime(0.001, now + 0.15);
  osc.connect(gain).connect(masterGain);
  osc.start(now);
  osc.stop(now + 0.15);

  // Stadium ring resonance
  const ring = c.createOscillator();
  ring.type = 'triangle';
  ring.frequency.setValueAtTime(1800, now);
  ring.frequency.exponentialRampToValueAtTime(600, now + 0.2);
  const ringGain = c.createGain();
  ringGain.gain.setValueAtTime(0.04, now);
  ringGain.gain.exponentialRampToValueAtTime(0.001, now + 0.25);
  const bp = c.createBiquadFilter();
  bp.type = 'bandpass';
  bp.frequency.setValueAtTime(1200, now);
  bp.Q.setValueAtTime(5, now);
  ring.connect(bp).connect(ringGain).connect(masterGain);
  ring.start(now);
  ring.stop(now + 0.25);
}

// ══════════════════════════════════════════════════════════════
//  LAUNCH — ripcord pull + whoosh
// ══════════════════════════════════════════════════════════════
export function playLaunch() {
  const c = ensureCtx();
  const now = c.currentTime;

  // Ripcord zip — ascending filtered noise
  const zipBuf = createNoiseBuffer(0.5, 3);
  const zip = c.createBufferSource();
  zip.buffer = zipBuf;
  const zipFilter = c.createBiquadFilter();
  zipFilter.type = 'bandpass';
  zipFilter.frequency.setValueAtTime(400, now);
  zipFilter.frequency.exponentialRampToValueAtTime(6000, now + 0.3);
  zipFilter.frequency.exponentialRampToValueAtTime(1500, now + 0.5);
  zipFilter.Q.setValueAtTime(3, now);
  const zipGain = c.createGain();
  zipGain.gain.setValueAtTime(0.18, now);
  zipGain.gain.linearRampToValueAtTime(0.22, now + 0.15);
  zipGain.gain.exponentialRampToValueAtTime(0.001, now + 0.5);
  zip.connect(zipFilter).connect(zipGain).connect(masterGain);
  zip.start(now);

  // Whoosh tone — sawtooth sweep
  const whoosh = c.createOscillator();
  whoosh.type = 'sawtooth';
  whoosh.frequency.setValueAtTime(80, now);
  whoosh.frequency.exponentialRampToValueAtTime(600, now + 0.25);
  whoosh.frequency.exponentialRampToValueAtTime(300, now + 0.45);
  const whooshGain = c.createGain();
  whooshGain.gain.setValueAtTime(0.04, now);
  whooshGain.gain.linearRampToValueAtTime(0.08, now + 0.2);
  whooshGain.gain.exponentialRampToValueAtTime(0.001, now + 0.45);
  whoosh.connect(whooshGain).connect(masterGain);
  whoosh.start(now);
  whoosh.stop(now + 0.45);

  // Mechanical click at start
  const click = c.createOscillator();
  click.type = 'square';
  click.frequency.setValueAtTime(4000, now);
  click.frequency.exponentialRampToValueAtTime(1000, now + 0.02);
  const clickGain = c.createGain();
  clickGain.gain.setValueAtTime(0.08, now);
  clickGain.gain.exponentialRampToValueAtTime(0.001, now + 0.03);
  click.connect(clickGain).connect(masterGain);
  click.start(now);
  click.stop(now + 0.03);
}

// ══════════════════════════════════════════════════════════════
//  BURST — explosive shatter with debris
// ══════════════════════════════════════════════════════════════
export function playBurst() {
  const c = ensureCtx();
  const now = c.currentTime;

  // Layer 1: low boom
  const boom = c.createOscillator();
  boom.type = 'sine';
  boom.frequency.setValueAtTime(80, now);
  boom.frequency.exponentialRampToValueAtTime(20, now + 0.3);
  const boomGain = c.createGain();
  boomGain.gain.setValueAtTime(0.25, now);
  boomGain.gain.exponentialRampToValueAtTime(0.001, now + 0.35);
  boom.connect(boomGain).connect(masterGain);
  boom.start(now);
  boom.stop(now + 0.35);

  // Layer 2: explosion noise
  const explBuf = createNoiseBuffer(0.5, 4);
  const expl = c.createBufferSource();
  expl.buffer = explBuf;
  const explGain = c.createGain();
  explGain.gain.setValueAtTime(0.3, now);
  explGain.gain.exponentialRampToValueAtTime(0.001, now + 0.5);
  expl.connect(explGain).connect(masterGain);
  expl.start(now);

  // Layer 3: high shatter — multiple short pings
  for (let i = 0; i < 5; i++) {
    const delay = i * 0.04 + Math.random() * 0.02;
    const ping = c.createOscillator();
    ping.type = 'sine';
    ping.frequency.setValueAtTime(2000 + Math.random() * 3000, now + delay);
    ping.frequency.exponentialRampToValueAtTime(500 + Math.random() * 500, now + delay + 0.08);
    const pingGain = c.createGain();
    pingGain.gain.setValueAtTime(0.04, now + delay);
    pingGain.gain.exponentialRampToValueAtTime(0.001, now + delay + 0.1);
    ping.connect(pingGain).connect(masterGain);
    ping.start(now + delay);
    ping.stop(now + delay + 0.1);
  }

  // Layer 4: debris rattle
  const rattleBuf = createNoiseBuffer(0.6, 6);
  const rattle = c.createBufferSource();
  rattle.buffer = rattleBuf;
  const rattleFilter = c.createBiquadFilter();
  rattleFilter.type = 'bandpass';
  rattleFilter.frequency.setValueAtTime(4000, now + 0.1);
  rattleFilter.Q.setValueAtTime(2, now);
  const rattleGain = c.createGain();
  rattleGain.gain.setValueAtTime(0.001, now);
  rattleGain.gain.linearRampToValueAtTime(0.08, now + 0.1);
  rattleGain.gain.exponentialRampToValueAtTime(0.001, now + 0.6);
  rattle.connect(rattleFilter).connect(rattleGain).connect(masterGain);
  rattle.start(now);
}

// ══════════════════════════════════════════════════════════════
//  COUNTDOWN — clean digital tone
// ══════════════════════════════════════════════════════════════
export function playCountdown() {
  const c = ensureCtx();
  const now = c.currentTime;

  const osc = c.createOscillator();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(880, now);
  const gain = c.createGain();
  gain.gain.setValueAtTime(0, now);
  gain.gain.linearRampToValueAtTime(0.1, now + 0.01); // fast attack
  gain.gain.setValueAtTime(0.1, now + 0.08);
  gain.gain.exponentialRampToValueAtTime(0.001, now + 0.25);
  osc.connect(gain).connect(masterGain);
  osc.start(now);
  osc.stop(now + 0.25);

  // Subtle harmonic
  const h = c.createOscillator();
  h.type = 'sine';
  h.frequency.setValueAtTime(1760, now);
  const hg = c.createGain();
  hg.gain.setValueAtTime(0, now);
  hg.gain.linearRampToValueAtTime(0.03, now + 0.01);
  hg.gain.exponentialRampToValueAtTime(0.001, now + 0.2);
  h.connect(hg).connect(masterGain);
  h.start(now);
  h.stop(now + 0.2);
}

// ══════════════════════════════════════════════════════════════
//  GO! — triumphant chord rising
// ══════════════════════════════════════════════════════════════
export function playGo() {
  const c = ensureCtx();
  const now = c.currentTime;

  // Power chord: root + fifth + octave
  const freqs = [440, 660, 880];
  freqs.forEach((f, i) => {
    const osc = c.createOscillator();
    osc.type = i === 0 ? 'sawtooth' : 'square';
    osc.frequency.setValueAtTime(f * 0.95, now);
    osc.frequency.linearRampToValueAtTime(f, now + 0.05);
    osc.frequency.setValueAtTime(f, now + 0.15);
    osc.frequency.linearRampToValueAtTime(f * 1.05, now + 0.35);
    const gain = c.createGain();
    gain.gain.setValueAtTime(0.06, now);
    gain.gain.setValueAtTime(0.06, now + 0.2);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.4);
    osc.connect(gain).connect(masterGain);
    osc.start(now);
    osc.stop(now + 0.4);
  });

  // Sweep noise
  const nBuf = createNoiseBuffer(0.3, 6);
  const n = c.createBufferSource();
  n.buffer = nBuf;
  const nf = c.createBiquadFilter();
  nf.type = 'bandpass';
  nf.frequency.setValueAtTime(2000, now);
  nf.frequency.linearRampToValueAtTime(6000, now + 0.2);
  nf.Q.setValueAtTime(2, now);
  const ng = c.createGain();
  ng.gain.setValueAtTime(0.06, now);
  ng.gain.exponentialRampToValueAtTime(0.001, now + 0.3);
  n.connect(nf).connect(ng).connect(masterGain);
  n.start(now);
}

// ══════════════════════════════════════════════════════════════
//  CONTINUOUS SPIN SOUND — layered whirring with harmonics
// ══════════════════════════════════════════════════════════════
let spinNodes = null;

export function startSpinSound() {
  const c = ensureCtx();
  if (spinNodes) return;

  // Multiple harmonics for rich whirring
  const oscs = [];
  const gains = [];
  const harmonics = [1, 2.01, 3.98, 5.02]; // slightly detuned for thickness
  const volumes = [0.03, 0.015, 0.008, 0.005];

  harmonics.forEach((h, i) => {
    const osc = c.createOscillator();
    osc.type = i === 0 ? 'sawtooth' : 'triangle';
    osc.frequency.setValueAtTime(60 * h, c.currentTime);
    const gain = c.createGain();
    gain.gain.setValueAtTime(0, c.currentTime);
    osc.connect(gain).connect(masterGain);
    osc.start(c.currentTime);
    oscs.push(osc);
    gains.push({ gain, baseVol: volumes[i] });
  });

  // Low rumble noise layer
  const rumbleLen = c.sampleRate * 2;
  const rumbleBuf = c.createBuffer(1, rumbleLen, c.sampleRate);
  const rumbleData = rumbleBuf.getChannelData(0);
  for (let i = 0; i < rumbleLen; i++) {
    rumbleData[i] = (Math.random() * 2 - 1);
  }
  const rumble = c.createBufferSource();
  rumble.buffer = rumbleBuf;
  rumble.loop = true;
  const rumbleFilter = c.createBiquadFilter();
  rumbleFilter.type = 'lowpass';
  rumbleFilter.frequency.setValueAtTime(200, c.currentTime);
  const rumbleGain = c.createGain();
  rumbleGain.gain.setValueAtTime(0, c.currentTime);
  rumble.connect(rumbleFilter).connect(rumbleGain).connect(masterGain);
  rumble.start(c.currentTime);

  spinNodes = { oscs, gains, rumble, rumbleFilter, rumbleGain };
}

export function updateSpinSound(rpm1, rpm2) {
  if (!spinNodes) return;
  const c = ensureCtx();
  const avgRPM = (rpm1 + rpm2) / 2;
  const rpmRatio = avgRPM / 10000;
  const baseFreq = 50 + rpmRatio * 120;

  const harmonics = [1, 2.01, 3.98, 5.02];
  spinNodes.oscs.forEach((osc, i) => {
    osc.frequency.setTargetAtTime(baseFreq * harmonics[i], c.currentTime, 0.1);
  });
  spinNodes.gains.forEach(({ gain, baseVol }) => {
    gain.gain.setTargetAtTime(baseVol * rpmRatio, c.currentTime, 0.1);
  });

  // Rumble intensity
  spinNodes.rumbleFilter.frequency.setTargetAtTime(100 + rpmRatio * 300, c.currentTime, 0.1);
  spinNodes.rumbleGain.gain.setTargetAtTime(0.02 * rpmRatio, c.currentTime, 0.1);
}

export function stopSpinSound() {
  if (!spinNodes) return;
  const c = ensureCtx();
  // Fade out
  spinNodes.gains.forEach(({ gain }) => {
    gain.gain.setTargetAtTime(0, c.currentTime, 0.05);
  });
  spinNodes.rumbleGain.gain.setTargetAtTime(0, c.currentTime, 0.05);

  // Stop after fade
  setTimeout(() => {
    if (!spinNodes) return;
    spinNodes.oscs.forEach(osc => { try { osc.stop(); } catch (e) {} });
    try { spinNodes.rumble.stop(); } catch (e) {}
    spinNodes = null;
  }, 200);
}
